import { Action, ActionByKeyEntry, Milestone } from './types';
import { createAction, createMilestone } from './api';
import { buildActionByKey, cleanForDuplication, generateUniqueCopyName } from './nba.utils';

export const duplicateSelectedMilestones = async (
  names: string[],
  milestones: Milestone[],
): Promise<Milestone[]> => {
  const existingNames = new Set(milestones.map((m) => m.name));

  let maxOrder = Math.max(0, ...milestones.map(({ order }) => order || 0));

  const nextMilestones: Milestone[] = [];
  for (const name of names) {
    const milestone = milestones.find((m) => m.name === name);
    if (!milestone) {
      continue;
    }

    const newName = generateUniqueCopyName(milestone.name, existingNames);
    existingNames.add(newName);
    maxOrder += 1;

    const nextMilestone: Milestone = {
      ...(cleanForDuplication(milestone) as Milestone),
      name: newName,
      label: milestone.label ?? milestone.name,
      order: maxOrder,
    };

    await createMilestone(nextMilestone);
    nextMilestones.push(nextMilestone);
  }

  return nextMilestones;
};

export const duplicateSelectedActions = async (
  params: {
    targetMilestoneName: string;
    targetStepName: string;
    selectedIds: Set<string>;
    milestones: Milestone[];
  }
): Promise<Action[]> => {
  const { targetMilestoneName, targetStepName, selectedIds, milestones } = params;
  const targetMilestone = milestones.find(({ name }) => name === targetMilestoneName);
  const targetStep = targetMilestone?.steps.find(({ name }) => name === targetStepName);

  if (!targetMilestone || !targetStep) {
    throw new Error('Target milestone/step not found.');
  }

  const existingNames = new Set(targetStep.actions.map(({ name }) => name));
  const baseMaxOrder = Math.max(0, ...targetStep.actions.map(({ order  }) => order ?? 0));
  const actionByKey = buildActionByKey(milestones);

  const sortedToCopy = Array.from(selectedIds)
    .map((id) => actionByKey.get(id))
    .filter((i): i is ActionByKeyEntry => Boolean(i))
    .sort((a, b) => (a.action.order ?? 0) - (b.action.order ?? 0));

  const nextActions: Action[] = [];
  for (let i = 0; i < sortedToCopy.length; i++) {
    const { action } = sortedToCopy[i];
    const { name: actionName } = action;
    const newName = generateUniqueCopyName(actionName, existingNames);
    existingNames.add(newName);

    const nextAction = {
      ...(cleanForDuplication(action) as Action),
      id: '',
      clientId: '',
      name: newName,
      order: baseMaxOrder + i + 1,
    };

    await createAction(targetMilestoneName, targetStepName, nextAction);
    nextActions.push(nextAction);
  }

  return nextActions;
};
